import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Grid,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Button,
  Box,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import StructuredModal from 'src/views/components/structuredModal/structuredModal';
import ControlledTextField from 'src/views/components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from 'src/views/components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from 'src/views/components/controlledComponents/ControlledDateField.component';
import ControlledFileUploadField from 'src/views/components/controlledComponents/ControlledFileUploadField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useHealthAndSafetyState,
  useHealthAndSafetyActions,
  EViewState,
} from '../../../../contexts/healthAndSafety.context';
import { useHealthAndSafety } from '../../../../hooks/useHealthAndSafety';
import { TSafetyProtocol } from 'src/apis/administration/administration.interfaces';
import { TKeyValue } from 'src/utils/commonInterfaces';
import dayjs, { Dayjs } from 'dayjs';
import {
  isPickerDateRangeOrdered,
  zOptionalNullablePickerDate,
  zRequiredPickerDate,
} from 'src/utils/zodFormHelpers';

type SafetyProtocolFormData = {
  protocolName: string;
  category:
  | 'FIRE_SAFETY'
  | 'FIRST_AID'
  | 'EMERGENCY_EVACUATION'
  | 'FOOD_SAFETY'
  | 'TRANSPORT_SAFETY'
  | 'LAB_SAFETY'
  | 'SPORTS_SAFETY';
  description: string;
  procedures: string[];
  responsiblePerson: string;
  lastUpdated: Dayjs | null;
  nextReviewDate: Dayjs | null;
  isActive: boolean;
  attachments?: any[];
  trainingRequired: boolean;
  lastTrainingDate?: Dayjs | null;
  nextTrainingDate?: Dayjs | null;
};

const getSafetyProtocolFormSchema = (translateText: (key: string) => string) =>
  z
    .object({
    protocolName: z.string().min(1, translateText('common.fieldRequired')),
    category: z.enum(
      [
        'FIRE_SAFETY',
        'FIRST_AID',
        'EMERGENCY_EVACUATION',
        'FOOD_SAFETY',
        'TRANSPORT_SAFETY',
        'LAB_SAFETY',
        'SPORTS_SAFETY',
      ],
      {
        required_error: translateText('common.fieldRequired'),
      }
    ),
    description: z.string().min(1, translateText('common.fieldRequired')),
    procedures: z
      .array(z.string())
      .min(
        1,
        translateText(
          'administration.healthAndSafety.validation.procedureRequired'
        )
      ),
    responsiblePerson: z.string().min(1, translateText('common.fieldRequired')),
    lastUpdated: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    nextReviewDate: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    isActive: z.boolean(),
    attachments: z.array(z.any()).optional(),
    trainingRequired: z.boolean(),
    lastTrainingDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    nextTrainingDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
  })
    .refine(
      (data) =>
        isPickerDateRangeOrdered(data.lastUpdated, data.nextReviewDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['nextReviewDate'],
      }
    )
    .refine(
      (data) =>
        !data.lastTrainingDate ||
        !data.nextTrainingDate ||
        isPickerDateRangeOrdered(
          data.lastTrainingDate,
          data.nextTrainingDate
        ),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['nextTrainingDate'],
      }
    );

const AddEditSafetyProtocolsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedProtocol, editMode, viewState, currentViewType } =
    useHealthAndSafetyState();
  const { setViewState, setEditMode, selectProtocol } =
    useHealthAndSafetyActions();
  const { createProtocol, updateProtocol, isLoading, errorMessage } =
    useHealthAndSafety();

  const isEditMode = editMode === 'edit' && selectedProtocol;
  const isOpen =
    (viewState === EViewState.ADD || viewState === EViewState.EDIT) &&
    currentViewType === 'protocols';

  const protocolFormSchema = getSafetyProtocolFormSchema(translateText);

  const defaultValues: SafetyProtocolFormData = {
    protocolName: '',
    category: 'FIRE_SAFETY',
    description: '',
    procedures: [''],
    responsiblePerson: '',
    lastUpdated: dayjs(),
    nextReviewDate: dayjs().add(1, 'year'),
    isActive: true,
    attachments: [],
    trainingRequired: false,
    lastTrainingDate: null,
    nextTrainingDate: null,
  };

  const methods = useForm<SafetyProtocolFormData>({
    resolver: zodResolver(protocolFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = methods;

  const procedures = watch('procedures') || [''];

  useEffect(() => {
    if (isEditMode && selectedProtocol) {
      const protocolProcedures = selectedProtocol.procedures || [''];
      reset({
        protocolName: selectedProtocol.protocolName,
        category: selectedProtocol.category,
        description: selectedProtocol.description,
        procedures: protocolProcedures.length > 0 ? protocolProcedures : [''],
        responsiblePerson: selectedProtocol.responsiblePerson,
        lastUpdated: selectedProtocol.lastUpdated
          ? dayjs(selectedProtocol.lastUpdated)
          : dayjs(),
        nextReviewDate: selectedProtocol.nextReviewDate
          ? dayjs(selectedProtocol.nextReviewDate)
          : dayjs().add(1, 'year'),
        isActive: selectedProtocol.isActive,
        attachments: selectedProtocol.attachments || [],
        trainingRequired: selectedProtocol.trainingRequired,
        lastTrainingDate: selectedProtocol.lastTrainingDate
          ? dayjs(selectedProtocol.lastTrainingDate)
          : null,
        nextTrainingDate: selectedProtocol.nextTrainingDate
          ? dayjs(selectedProtocol.nextTrainingDate)
          : null,
      });
    } else if (isOpen && !isEditMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, selectedProtocol, isOpen, reset]);

  const handleClose = () => {
    if (isEditMode) {
      // Return to details view when canceling from edit mode
      setViewState(EViewState.DETAILS);
    } else {
      // Return to list view when canceling from add mode
      setViewState(EViewState.LIST);
      selectProtocol(undefined);
    }
    setEditMode(undefined);
    reset(defaultValues);
  };

  const addProcedure = () => {
    const newProcedures = [...procedures, ''];
    setValue('procedures', newProcedures, { shouldValidate: true });
  };

  const removeProcedure = (index: number) => {
    const newProcedures = procedures.filter((_, i) => i !== index);
    setValue('procedures', newProcedures.length > 0 ? newProcedures : [''], {
      shouldValidate: true,
    });
  };

  // const updateProcedure = (index: number, value: string) => {
  //   const newProcedures = [...procedures];
  //   newProcedures[index] = value;
  //   setValue('procedures', newProcedures, { shouldValidate: true });
  // };

  const onSubmitForm = async (data: SafetyProtocolFormData) => {
    try {
      const protocolData: Omit<
        TSafetyProtocol,
        'id' | 'createdAt' | 'updatedAt'
      > = {
        protocolName: data.protocolName,
        category: data.category,
        description: data.description,
        procedures: data.procedures.filter((p) => p.trim() !== ''),
        responsiblePerson: data.responsiblePerson,
        lastUpdated: data.lastUpdated ? data.lastUpdated.toDate() : new Date(),
        nextReviewDate: data.nextReviewDate
          ? data.nextReviewDate.toDate()
          : new Date(),
        isActive: data.isActive,
        attachments: data.attachments,
        trainingRequired: data.trainingRequired,
        lastTrainingDate: data.lastTrainingDate
          ? data.lastTrainingDate.toDate()
          : undefined,
        nextTrainingDate: data.nextTrainingDate
          ? data.nextTrainingDate.toDate()
          : undefined,
      };

      if (isEditMode && selectedProtocol) {
        await updateProtocol(selectedProtocol.id, protocolData);
      } else {
        await createProtocol(protocolData);
      }

      handleClose();
    } catch (error) {
      console.error('Error saving safety protocol:', error);
    }
  };

  const categoryOptions: TKeyValue<string>[] = [
    {
      key: 'FIRE_SAFETY',
      value: translateText('administration.healthAndSafety.values.fireSafety'),
    },
    {
      key: 'FIRST_AID',
      value: translateText('administration.healthAndSafety.values.firstAid'),
    },
    {
      key: 'EMERGENCY_EVACUATION',
      value: translateText(
        'administration.healthAndSafety.values.emergencyEvacuation'
      ),
    },
    {
      key: 'FOOD_SAFETY',
      value: translateText('administration.healthAndSafety.values.foodSafety'),
    },
    {
      key: 'TRANSPORT_SAFETY',
      value: translateText(
        'administration.healthAndSafety.values.transportSafety'
      ),
    },
    {
      key: 'LAB_SAFETY',
      value: translateText('administration.healthAndSafety.values.labSafety'),
    },
    {
      key: 'SPORTS_SAFETY',
      value: translateText(
        'administration.healthAndSafety.values.sportsSafety'
      ),
    },
  ];

  return (
    <div data-testid="add-edit-safety-protocols-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText(
              'administration.healthAndSafety.editSafetyProtocols'
            ) || 'Edit Safety Protocol'
            : translateText(
              'administration.healthAndSafety.addSafetyProtocols'
            ) || 'Add Safety Protocol'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={isLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          errorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="safety-protocol-error-message"
            >
              {errorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="safety-protocol-form"
          >
            <Grid container spacing={3} padding={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {translateText(
                    'administration.healthAndSafety.labels.basicInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="protocolName"
                  label={translateText(
                    'administration.healthAndSafety.columns.protocolName'
                  )}
                  name="protocolName"
                  control={control}
                  errorMessage={errors.protocolName?.message}
                  required
                  data-testid="protocol-name-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="category"
                  label={translateText(
                    'administration.healthAndSafety.columns.category'
                  )}
                  name="category"
                  control={control}
                  itemList={categoryOptions}
                  errorMessage={errors.category?.message}
                  isRequired
                  data-testid="category-field"
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="description"
                  label={translateText(
                    'administration.healthAndSafety.columns.description'
                  )}
                  name="description"
                  control={control}
                  errorMessage={errors.description?.message}
                  multiline
                  rows={3}
                  required
                  data-testid="description-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="responsiblePerson"
                  label={translateText(
                    'administration.healthAndSafety.columns.responsible'
                  )}
                  name="responsiblePerson"
                  control={control}
                  errorMessage={errors.responsiblePerson?.message}
                  required
                  data-testid="responsible-person-field"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.healthAndSafety.labels.procedures'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {procedures.map((procedure, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                    >
                      <ControlledTextField
                        id={`procedure-${index}`}
                        label={`${translateText('administration.healthAndSafety.labels.procedure')} ${index + 1}`}
                        name={`procedures.${index}`}
                        control={control}
                        multiline
                        rows={2}
                        isGridItem={false}
                        data-testid={`procedure-field-${index}`}
                      />
                      {procedures.length > 1 && (
                        <Button
                          onClick={() => removeProcedure(index)}
                          color="error"
                          sx={{ mt: 1 }}
                          data-testid={`remove-procedure-${index}`}
                        >
                          <DeleteIcon />
                        </Button>
                      )}
                    </Box>
                  ))}
                  <Button
                    onClick={addProcedure}
                    variant="outlined"
                    startIcon={<AddIcon />}
                    data-testid="add-procedure-button"
                  >
                    {translateText(
                      'administration.healthAndSafety.labels.addProcedure'
                    )}
                  </Button>
                </Box>
                {errors.procedures && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.procedures.message}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.healthAndSafety.labels.datesTimeline'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledDateField
                  id="lastUpdated"
                  label={translateText(
                    'administration.healthAndSafety.columns.lastUpdated'
                  )}
                  name="lastUpdated"
                  control={control}
                  errorMessage={errors.lastUpdated?.message}
                  required
                  data-testid="last-updated-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledDateField
                  id="nextReviewDate"
                  label={translateText(
                    'administration.healthAndSafety.columns.nextReview'
                  )}
                  name="nextReviewDate"
                  control={control}
                  errorMessage={errors.nextReviewDate?.message}
                  required
                  data-testid="next-review-date-field"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.healthAndSafety.labels.trainingInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={methods.watch('trainingRequired')}
                      onChange={(e) =>
                        methods.setValue('trainingRequired', e.target.checked)
                      }
                      data-testid="training-required-switch"
                    />
                  }
                  label={translateText(
                    'administration.healthAndSafety.columns.training'
                  )}
                />
              </Grid>

              {methods.watch('trainingRequired') && (
                <>
                  <Grid item xs={12} md={6}>
                    <ControlledDateField
                      id="lastTrainingDate"
                      label={translateText(
                        'administration.healthAndSafety.labels.lastTrainingDate'
                      )}
                      name="lastTrainingDate"
                      control={control}
                      errorMessage={errors.lastTrainingDate?.message}
                      data-testid="last-training-date-field"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ControlledDateField
                      id="nextTrainingDate"
                      label={translateText(
                        'administration.healthAndSafety.labels.nextTrainingDate'
                      )}
                      name="nextTrainingDate"
                      control={control}
                      errorMessage={errors.nextTrainingDate?.message}
                      data-testid="next-training-date-field"
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={methods.watch('isActive')}
                      onChange={(e) =>
                        methods.setValue('isActive', e.target.checked)
                      }
                      data-testid="is-active-switch"
                    />
                  }
                  label={translateText(
                    'administration.healthAndSafety.status.active'
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledFileUploadField
                  id="attachments"
                  label={translateText(
                    'administration.healthAndSafety.labels.attachments'
                  )}
                  name="attachments"
                  control={control}
                  multiple
                  data-testid="attachments-field"
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditSafetyProtocolsView;
